import { StyledEngineProvider } from '@mui/material';
import { QueryClient } from '@tanstack/react-query';
import { Outlet, createRootRouteWithContext } from '@tanstack/react-router';
import { Suspense, useEffect } from 'react';
import { Toaster } from 'sonner';

import { Devtools } from '@shared/components/devtools/Devtools';
import '@shared/theme/mui/base/density.css';
import '@shared/theme/mui/base/fonts.css';
import '@shared/theme/mui/base/reset.css';
import '@shared/theme/mui/base/variables.css';

import { useAppUpdateNotice } from 'component/hooks/useAppUpdateNotice';
import { useErrorSnackbarState } from 'component/hooks/useErrorSnackbarState';
import { useResetMainContentScroll } from 'component/hooks/useResetMainContentScroll';
import { useSetRouteTitle } from 'component/hooks/useSetRouteTitle';
import { AutoThemeProvider } from 'component/root/AutoThemeProvider';
import { ErrorHandler } from 'component/root/ErrorHandler';
import { NotFoundMessage } from 'component/view/Redirects/NotFoundMessage';

import '../theme/variables.css';

export interface RouterContext {
  queryClient: QueryClient;
  ensureQueryData: QueryClient['ensureQueryData'];
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFoundMessage,
});

function RootComponent() {
  useSetRouteTitle();
  useErrorSnackbarState();
  useAppUpdateNotice();
  useResetMainContentScroll();

  // Hide the initial loading spinner after the first paint.
  useEffect(() => {
    window.hideLoadingSpinner(true);
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <AutoThemeProvider>
        <Toaster position="top-right" />
        <ErrorHandler>
          <Suspense fallback={null}>
            <Outlet />
          </Suspense>
          <Devtools />
        </ErrorHandler>
      </AutoThemeProvider>
    </StyledEngineProvider>
  );
}
